// ============================================================
// HOUSE OF CARDS — AudioAssets v1 — THE SOUND OF THE HOUSE
// ------------------------------------------------------------
// Pure data. The single source of truth for every sound in the
// game: its file, its role, how loud it sits, whether it loops.
// No React, no DOM work at import time — safe to import anywhere.
//
// THE TRACKS (18 total, all ~5–10s, all looped where it matters):
//   • menu .............. title screen / pause / pockets / settings
//   • 7 room ambiences .. one per explorable room; HARD-CUT on move
//   • 6 HEARTBEATS ...... one slot, one pulse at a time:
//        – 5 psychosis tiers (0 slow → 4 fast), tied to the meter
//        – 1 STAR POWER pulse: the secret sixth heartbeat, earned via
//          the toy-in-gold-box ritual; it TAKES OVER the slot from the
//          tier pulse (never layered), and drops back to the current
//          tier on death/loss.
//   • 3 jumpscare stings  real threat · masquerade · Tome's Shadow
//   • mushroom rave ..... a SEPARATE full-takeover party loop (NOT a
//        room or heartbeat track); the only thing that pauses the
//        heartbeat slot, for its 2 minutes.
//
// FILE LOCATION: drop the audio under public/audio/ in the CRA
// project so these absolute paths resolve at runtime. Swap the
// extension here in ONE place if you deliver .wav instead of .mp3
// (set AUDIO_EXT below). caP'n: change one line, not sixteen. <3
// ============================================================

// Flip to ".wav" if you hand me wavs instead of mp3s — every path
// below is built from this, so it's a one-line switch.
export const AUDIO_EXT = ".mp3";

// Where the files live (served statically by CRA from /public).
const DIR = "/audio/";

// Small helper so the registry stays tidy and the ext stays central.
const src = (name) => `${DIR}${name}${AUDIO_EXT}`;

// ── ROLES ─────────────────────────────────────────────────────
// The mixer treats tracks differently by role:
//   menu .......... exclusive; silences the house while open
//   ambience ...... one at a time; HARD-CUT when the room changes
//   heartbeat ..... ALWAYS on over everything; never interrupted
//   jumpscare ..... one-shot sting; priority decides who wins
//   special ....... star power / mushroom — take over the bed
export const ROLE = {
  MENU: "menu",
  AMBIENCE: "ambience",
  HEARTBEAT: "heartbeat",
  JUMPSCARE: "jumpscare",
  SPECIAL: "special",
};

// ── HEARTBEAT TIERS ───────────────────────────────────────────
// FIVE pulses, now matched 1:1 to the meter's five visual tiers
// (PsychosisEngine.PSY_TIERS). Same 20% bands, same upper-bound (<=)
// edges, so the pulse track and the HUD tier change on the exact same
// tick. Volume climbs with the tier — by tier 4 the heart is in your
// throat.
//   tier : [ rawPct inclusive upper edge, volume ]
export const HEARTBEAT_TIERS = [
  { tier: 0, toPct: 20,  volume: 0.40 }, //  0–20%   slowest  (LUCID)
  { tier: 1, toPct: 40,  volume: 0.50 }, // 20–40%            (FRAYING)
  { tier: 2, toPct: 60,  volume: 0.65 }, // 40–60%            (SLIPPING)
  { tier: 3, toPct: 80,  volume: 0.80 }, // 60–80%            (SPLITTING)
  { tier: 4, toPct: 100, volume: 0.90 }, // 80–100% fastest   (BREAKING)
];

// Map a raw psychosis percentage (0..100) to a heartbeat tier (0..4).
// Uses upper-bound (<=) bands so it is IDENTICAL to the HUD's tierForPct
// in PsychosisEngine — the pulse and the meter step together, no
// one-tick disagreement at the 20/40/60/80 boundaries.
export function heartbeatTierForPct(pct) {
  const p = Math.max(0, Math.min(100, pct || 0));
  // toPct is the inclusive UPPER edge of each tier's band.
  for (let i = 0; i < HEARTBEAT_TIERS.length; i++) {
    if (p <= HEARTBEAT_TIERS[i].toPct) return HEARTBEAT_TIERS[i].tier;
  }
  return HEARTBEAT_TIERS[HEARTBEAT_TIERS.length - 1].tier;
}

// ── JUMPSCARE PRIORITY ────────────────────────────────────────
// If two stings ever race, the higher number wins and cuts the
// other. Real threat is loudest in the mix and in the hierarchy;
// the Shadow sting sits underneath the masquerade so a genuine
// fright is never stepped on by a lesser one.
export const JUMPSCARE_PRIORITY = {
  real: 3,        // monster lunging at the lens (tenant, maw)
  masquerade: 2,  // a toy / a falling frame — the hype-man beats
  shadow: 1,      // Tome's Shadow / the crib child — the quiet dread
};

// ── ROOM → AMBIENCE TRACK ─────────────────────────────────────
// The seven explorable rooms each get a bed. The Mouth Door is NOT
// a room (it's a hotspot in the hall) — its "track" is Star Power,
// fired at the finale, so it isn't listed here.
export const ROOM_TRACK = {
  hall:    "room_hall",
  nursery: "room_nursery",
  study:   "room_study",
  kitchen: "room_kitchen",
  bath:    "room_bath",
  master:  "room_master",
  cellar:  "room_cellar",
};

// ============================================================
// THE REGISTRY — every track keyed by its id.
// id matches the string the engine plays by; keep ids stable.
// ============================================================
export const AUDIO_TRACKS = {
  // ── Menu / pause bed ──────────────────────────────────────
  menu: { id: "menu", src: src("menu"), role: ROLE.MENU, loop: true, volume: 0.80 },

  // ── Room ambiences (hard-cut on room change) ──────────────
  room_hall:    { id: "room_hall",    src: src("room_hall"),    role: ROLE.AMBIENCE, loop: true, volume: 0.60 },
  room_nursery: { id: "room_nursery", src: src("room_nursery"), role: ROLE.AMBIENCE, loop: true, volume: 0.60 },
  room_study:   { id: "room_study",   src: src("room_study"),   role: ROLE.AMBIENCE, loop: true, volume: 0.60 },
  room_kitchen: { id: "room_kitchen", src: src("room_kitchen"), role: ROLE.AMBIENCE, loop: true, volume: 0.60 },
  room_bath:    { id: "room_bath",    src: src("room_bath"),    role: ROLE.AMBIENCE, loop: true, volume: 0.60 },
  room_master:  { id: "room_master",  src: src("room_master"),  role: ROLE.AMBIENCE, loop: true, volume: 0.60 },
  room_cellar:  { id: "room_cellar",  src: src("room_cellar"),  role: ROLE.AMBIENCE, loop: true, volume: 0.60 },

  // ── Heartbeat tiers (over everything, never cut) ──────────
  heartbeat_0: { id: "heartbeat_0", src: src("heartbeat_tier0"), role: ROLE.HEARTBEAT, loop: true, volume: 0.40 },
  heartbeat_1: { id: "heartbeat_1", src: src("heartbeat_tier1"), role: ROLE.HEARTBEAT, loop: true, volume: 0.50 },
  heartbeat_2: { id: "heartbeat_2", src: src("heartbeat_tier2"), role: ROLE.HEARTBEAT, loop: true, volume: 0.65 },
  heartbeat_3: { id: "heartbeat_3", src: src("heartbeat_tier3"), role: ROLE.HEARTBEAT, loop: true, volume: 0.80 },
  heartbeat_4: { id: "heartbeat_4", src: src("heartbeat_tier4"), role: ROLE.HEARTBEAT, loop: true, volume: 0.90 },

  // ── Jumpscare stings (one-shot, priority-gated) ───────────
  scare_real:       { id: "scare_real",       src: src("jumpscare"), role: ROLE.JUMPSCARE, loop: false, volume: 1.00, priority: JUMPSCARE_PRIORITY.real },
  scare_masquerade: { id: "scare_masquerade", src: src("jumpscare"), role: ROLE.JUMPSCARE, loop: false, volume: 1.00, priority: JUMPSCARE_PRIORITY.masquerade },
  scare_shadow:     { id: "scare_shadow",     src: src("jumpscare"), role: ROLE.JUMPSCARE, loop: false, volume: 1.00, priority: JUMPSCARE_PRIORITY.shadow },

  // ── Specials ──────────────────────────────────────────────
  // star_power is the SECRET SIXTH HEARTBEAT: earned via the toy-in-
  // gold-box ritual, it takes over the single heartbeat slot from the
  // psychosis-tier pulse (never layered on top of it). Drops back to
  // the current psychosis tier on death/loss. The rave is a wholly
  // separate full-takeover party loop — NOT a room/heartbeat track.
  star_power:    { id: "star_power",    src: src("star_power"),    role: ROLE.HEARTBEAT, loop: true, volume: 0.85 },
  mushroom_rave: { id: "mushroom_rave", src: src("heartbeat_rave"), role: ROLE.SPECIAL,   loop: true, volume: 0.85 },
};

// ── SCARE FIEND → STING TYPE ──────────────────────────────────
// Maps the JumpscareEngine's `fiend` / `kind` onto which of the
// three stings plays. The crib child is Tome's Shadow → shadow
// sting. Tenant & maw are the real thing. Toy & frame are the
// masquerade. A clean lookup so App stays dumb about audio.
export const FIEND_STING = {
  tenant: "real",
  maw:    "real",
  child:  "shadow",     // the crib_shadow_child — Tome's Shadow
  toy:    "masquerade",
  frame:  "masquerade",
};

// Resolve a scare object → sting type ("real" | "masquerade" | "shadow").
// Falls back to kind, then to "shadow" (the quietest) so a missing
// mapping never plays the loudest sting by accident.
export function stingForScare(scare) {
  if (!scare) return "shadow";
  if (scare.fiend && FIEND_STING[scare.fiend]) return FIEND_STING[scare.fiend];
  if (scare.kind === "monster") return "real";
  if (scare.kind === "masquerade") return "masquerade";
  return "shadow";
}

// The sting trackId for a given type.
export const STING_TRACK = {
  real: "scare_real",
  masquerade: "scare_masquerade",
  shadow: "scare_shadow",
};

// A flat list of every track (handy for preloading / validation).
export const ALL_TRACKS = Object.values(AUDIO_TRACKS);
